// Sun-preference normalization.
//
// The LLM occasionally returns compound values as a single string —
// e.g. "partial_sun.shade", "full_sun, shade", "partial_sun/shade" — instead
// of an array of atomic values. Rendering code translates individual values
// (`full_sun`, `partial_sun`, `shade`), so a compound string leaks the raw
// key into the UI (users see literal "partial_sun.shade" text).
//
// This helper accepts anything (string / array / null / undefined) and returns
// a de-duplicated array of atomic tokens, each of which is one of the three
// canonical sun keys when possible, or the raw value if the LLM produced
// something unrecognized (rare — surfaced verbatim rather than crashing).

const CANONICAL = new Set(["full_sun", "partial_sun", "shade"]);
const SPLIT_RE = /[.,;/|\s]+/;

const coerce = (raw) => {
  if (!raw && raw !== 0) return "";
  return String(raw).trim().toLowerCase().replace(/\s+/g, "_");
};

export const normalizeSunPreference = (value) => {
  if (value == null || value === "") return [];
  const list = Array.isArray(value) ? value : [value];
  const out = [];
  const seen = new Set();
  for (const entry of list) {
    const parts = String(entry).split(SPLIT_RE);
    for (const p of parts) {
      const tok = coerce(p);
      if (!tok) continue;
      // Best-effort: map any obvious alias to a canonical value; leave others as-is.
      let canon = tok;
      if (tok === "full" || tok === "sun") canon = "full_sun";
      else if (tok === "partial" || tok === "part" || tok === "partial_shade") canon = "partial_sun";
      else if (tok === "shady" || tok === "full_shade") canon = "shade";
      if (seen.has(canon)) continue;
      seen.add(canon);
      out.push(canon);
    }
  }
  // Prefer canonical ordering: full → partial → shade → anything else.
  const order = { full_sun: 0, partial_sun: 1, shade: 2 };
  out.sort((a, b) => (order[a] ?? 99) - (order[b] ?? 99));
  return out;
};

export const isCanonicalSunPref = (s) => CANONICAL.has(s);
